/**
 * MkUpgradePanel — hull-refit screen at the mechanical yard. Shows the CURRENT
 * hull on the left and the NEXT Mk on the right, side by side, with what the
 * refit unlocks and a Refit button (gated on cost, energy cores + prerequisites).
 */
import type { HudSnapshot } from "@/engine/GameState";

const BASE = import.meta.env.BASE_URL;

interface Hull {
  name: string;
  mk: string;
  sprite: string;
  traits: string[];
}
const HULLS: Hull[] = [
  {
    name: "Miner",
    mk: "I",
    sprite: "MTS_1",
    traits: ["1 mining beam", "No turrets", "No drones", "Nimble & quick"],
  },
  {
    name: "Carrier",
    mk: "II",
    sprite: "MTS_2",
    traits: [
      "Mining +30%, range +20%",
      "4 turret ports",
      "Unlocks drone & repair bays",
      "Speed −30%",
    ],
  },
  {
    name: "Carrier",
    mk: "III",
    sprite: "MTS_3",
    traits: [
      "Mining +40%, range +30%",
      "8 turret ports",
      "Side drone launch gates",
      "+1 drone, yield +25%",
    ],
  },
  {
    name: "Carrier",
    mk: "IV",
    sprite: "MTS_4",
    traits: [
      "2× hull size, plows asteroids",
      "8 turrets + 3 spinal cannons",
      "Fighter bays + 360° mining upgrades",
      "Ponderously slow (−50% speed)",
    ],
  },
  // Index 4 = Battleship Mk V, index 5 = Swarm-lord Mk V (chosen in the Upgrades tree).
  {
    name: "Battleship",
    mk: "V",
    sprite: "MTS_V_BTS",
    traits: [
      "20% bigger hull",
      "14 turrets, 4 cannons + 2 Big Cannons",
      "Map-length beam guns; shoots to mine",
      "8 fighters",
    ],
  },
  {
    name: "Swarm-lord",
    mk: "V",
    sprite: "MTS_V_SWL",
    traits: [
      "20% bigger hull",
      "12 fighters + 12 fighter drones",
      "14 turrets, 2 cannons, wing runways",
      "Defensive spinning rings",
    ],
  },
  // Index 6 = Battleship Mk VI (the Battleship line's next hull).
  {
    name: "Battleship",
    mk: "VI",
    sprite: "MTS_VI_BTS",
    traits: [
      "Bigger, heavier battleship",
      "18 turrets, 8 cannons + 4 Big Cannons",
      "6 missile silos (upgradable salvo)",
      "+250 max shield",
    ],
  },
  // Index 7 = Swarm-lord Mk VI (the Swarm-lord line's next hull).
  {
    name: "Swarm-lord",
    mk: "VI",
    sprite: "MTS_VI_SWL",
    traits: [
      "Bigger drone carrier",
      "4 wing hangars · up to 18 drones",
      "14 turrets, 4 cannons, wing runways",
      "+150 shield · no Big Cannons",
    ],
  },
  // Index 8 = Swarm-lord Mk VII (the cosmic apex of the Swarm-lord line).
  {
    name: "Swarm-lord",
    mk: "VII",
    sprite: "MTS_VII_SWL",
    traits: [
      "Cosmic apex carrier (+20% size)",
      "8 wing hangars · full drone swarm",
      "12 turrets, 8 cannons, 12 missiles",
      "+250 shield · no Big Cannons",
    ],
  },
];

function fmt(n: number): string {
  if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "k";
  return Math.floor(n).toString();
}

interface Props {
  snap: HudSnapshot;
  onRefit: (id: string) => void;
  onClose: () => void;
}

export default function MkUpgradePanel({ snap, onRefit, onClose }: Props) {
  const r = snap.refit;
  const cur =
    r.tier >= 5
      ? snap.arsenal.mkvPath === "swarmlord"
        ? (snap.arsenal.isMkviiSwl ? HULLS[8]! : HULLS[7]!)
        : HULLS[6]!
      : r.tier >= 4
        ? snap.arsenal.mkvPath === "battleship"
          ? HULLS[4]!
          : HULLS[5]!
        : (HULLS[r.tier] ?? HULLS[0]!);
  // Mk V → Mk VI along the same path (Battleship HULLS[6] / Swarm-lord HULLS[7]); lower tiers advance by index.
  const next = !r.nextId
    ? null
    : r.nextId === "mkvi_bts"
      ? HULLS[6]!
      : r.nextId === "mkvi_swl"
        ? HULLS[7]!
        : r.nextId === "mkvii_swl"
          ? HULLS[8]!
          : HULLS[r.tier + 1];
  const branch = r.branchIds; // Carrier → two Mk V path ids
  const affordMinerals = snap.minerals >= r.cost;
  const affordUnits = snap.units >= r.units;
  const affordCores = r.coresHave >= r.cores;
  const canBuy =
    !!next && r.reqsMet && r.atMechanical && affordMinerals && affordUnits && affordCores;
  const canBuyBranch =
    r.reqsMet && r.atMechanical && affordMinerals && affordUnits && affordCores;
  const branchHull = (id: string): Hull => (id === "mkv_bts" ? HULLS[4]! : HULLS[5]!);

  const hullCard = (h: Hull, side: "current" | "next") => (
    <div className={`refit-card ${side}`}>
      <div className={`refit-badge ${side}`}>
        {side === "current" ? "CURRENT" : "NEXT"}
      </div>
      <img className="refit-ship" src={`${BASE}sprites/${h.sprite}.png`} alt="" />
      <div className="refit-name">
        {h.name} <span className="refit-mk">Mk {h.mk}</span>
      </div>
      <ul className="refit-traits">
        {h.traits.map((t, i) => (
          <li key={i}>{t}</li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="stats-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Hull Refit"
      >
        <div className="upg-head">
          <div className="upg-head-title">🚀 Hull Refit</div>
          <button className="upg-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="stats-body">
          {branch ? (
            <>
              <div className="refit-compare">
                {hullCard(cur, "current")}
                <div className="refit-arrow">➜</div>
                <div className="refit-branch">
                  {branch.map((id) => (
                    <div key={id} className="refit-branch-choice">
                      {hullCard(branchHull(id), "next")}
                      <button
                        className="hud-btn accent refit-btn"
                        disabled={!canBuyBranch}
                        onClick={() => onRefit(id)}
                      >
                        Refit
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="refit-cost">
                Cost: <b className={affordMinerals ? "" : "bad"}>◆ {fmt(r.cost)}</b> ·{" "}
                <b className={affordUnits ? "" : "bad"}>◇ {fmt(r.units)} U</b>
                {r.cores > 0 && (
                  <>
                    {" "}
                    · <b className={affordCores ? "" : "bad"}>⬡ {r.coresHave}/{r.cores} Cores</b>
                  </>
                )}
              </div>
              <div className="refit-hint">Pick ONE — the other path locks permanently.</div>
              {!r.atMechanical && (
                <div className="refit-hint">Dock at a 🔧 mechanical yard to refit.</div>
              )}
              {r.atMechanical && (!affordMinerals || !affordUnits || !affordCores) && (
                <div className="refit-hint">Not enough materials yet.</div>
              )}
            </>
          ) : (
            <>
              <div className="refit-compare">
                {hullCard(cur, "current")}
                <div className="refit-arrow">➜</div>
                {next ? (
                  hullCard(next, "next")
                ) : (
                  <div className="refit-card next maxed">
                    <div className="refit-badge next">MAX</div>
                    <div className="refit-name">Top hull reached</div>
                    <div className="refit-max-note">This is the top hull.</div>
                  </div>
                )}
              </div>

              {next && (
                <>
                  <div className="refit-cost">
                    Cost: <b className={affordMinerals ? "" : "bad"}>◆ {fmt(r.cost)}</b>
                    {r.units > 0 && (
                      <>
                        {" "}
                        · <b className={affordUnits ? "" : "bad"}>◇ {fmt(r.units)} U</b>
                      </>
                    )}
                    {r.cores > 0 && (
                      <>
                        {" "}
                        ·{" "}
                        <b className={affordCores ? "" : "bad"}>
                          ⬡ {r.coresHave}/{r.cores} Energy Cores
                        </b>
                      </>
                    )}
                  </div>
                  <button
                    className="hud-btn accent refit-btn"
                    disabled={!canBuy}
                    onClick={() => r.nextId && onRefit(r.nextId)}
                  >
                    Refit to {r.nextName ?? "next hull"}
                  </button>
                  {!r.atMechanical && (
                    <div className="refit-hint">Dock at a 🔧 mechanical yard to refit.</div>
                  )}
                  {r.atMechanical && !r.reqsMet && (
                    <div className="refit-hint">
                      Buy the prerequisite upgrades first (see the ⚙ Upgrades tree).
                    </div>
                  )}
                  {r.atMechanical && r.reqsMet && (!affordMinerals || !affordCores) && (
                    <div className="refit-hint">
                      Not enough {!affordMinerals ? "minerals" : ""}
                      {!affordMinerals && !affordCores ? " & " : ""}
                      {!affordCores ? "energy cores" : ""}.
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
